import React, { useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import {
  useRestApiProvider,
  WhatsappBusinessVerificationStatus,
} from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import {
  CRMAccountCard,
  CRMAccountCardProps,
} from "../../../../components/CRMAccountCard";
import { ReactComponent as BusinessIcon } from "./assets/business.svg";
import { ReactComponent as ClockIcon } from "./assets/clock.svg";
import { ReactComponent as DocumentIcon } from "./assets/document.svg";
import { ReactComponent as MessageIcon } from "./assets/message.svg";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { getBusinessVerificationStatusDetails } from "../../../../utils/getBusinessVerificationStatusDetails";

export const WhatsappBusinessVerificationTab: React.FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { whatsappAgent } = useWhatsappAccount();

  const { data, isLoading, isFetching, isError, refetch } =
    integrationsApi.useGetWhatsappBusinessVerificationStatusQuery({
      entityId,
    });

  const cardProps: Omit<CRMAccountCardProps, "title" | "icon"> | undefined =
    useMemo(() => {
      if (!data || !whatsappAgent) return undefined;

      const handleButtonClick = () => {
        window.open(
          `https://business.facebook.com/latest/settings/security_center?business_id=${whatsappAgent.waba_id}`,
          "_blank"
        );
      };

      const { description, status } = getBusinessVerificationStatusDetails(
        data.status,
        t
      );

      const verifyNowButton = {
        text: t({
          id: "client-support-page.verify-now",
          defaultMessage: "Verify now",
        }),
        onClick: handleButtonClick,
      };

      const goToMetaButton = {
        text: t({
          id: "client-support-page.go-to-meta",
          defaultMessage: "Go to Meta",
        }),
        onClick: handleButtonClick,
      };

      const buttonMap: Partial<
        Record<WhatsappBusinessVerificationStatus, typeof verifyNowButton>
      > = {
        not_verified: verifyNowButton,
        expired: verifyNowButton,
        pending_need_more_info: goToMetaButton,
        pending_submission: goToMetaButton,
        failed: goToMetaButton,
        ineligible: goToMetaButton,
        rejected: goToMetaButton,
        revoked: goToMetaButton,
      };

      const buttonProps = buttonMap[data.status];

      return {
        description,
        status,
        buttonProps,
      };
    }, [data, t, whatsappAgent]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading || isFetching}
      onRetry={refetch}
    >
      {cardProps && (
        <CRMAccountCard
          title={t({
            id: "client-support-page.meta-business-verification",
            defaultMessage: "Meta Business Verification",
          })}
          icon={BusinessIcon}
          {...cardProps}
        />
      )}
      <CRMInfoSection
        title={t({
          id: "whatsapp-account-page.tools-and-tips",
          defaultMessage: "Tools & Tips",
        })}
        subtitle={t({
          id: "whatsapp-account-page.everything-you-need-to-know",
          defaultMessage: "Everything you need to know:",
        })}
        items={[
          {
            icon: <DocumentIcon />,
            title: t({
              id: "whatsapp-account-page.business-verification-requires-documents",
              defaultMessage: "Business Verification requires next documents:",
            }),
            tips: [
              t({
                id: "whatsapp-account-page.business-legal-name",
                defaultMessage:
                  "Document to verify Legal Business Name Example - Business Registration/Company Incorporation Certificate",
              }),
              t({
                id: "whatsapp-account-page.business-legal-address",
                defaultMessage:
                  "Document to verify Legal Business Address Example - Bank Statement/ Company Incorporation Certificate",
              }),
              t({
                id: "whatsapp-account-page.business-docs-match",
                defaultMessage:
                  "Both documents should have the exact same legal business name and address",
              }),
            ],
          },
          {
            icon: <ClockIcon />,
            title: t({
              id: "whatsapp-account-page.time-taken-for-business-verification",
              defaultMessage: "Time Taken for Business Verification:",
            }),
            description: t({
              id: "whatsapp-account-page.time-taken-for-business-verification-description",
              defaultMessage:
                "Usually, Meta team takes about 3-7 working days for business verification",
            }),
          },
          {
            icon: <MessageIcon />,
            title: t({
              id: "whatsapp-account-page.messaging-limit-without-business-verification",
              defaultMessage: "Messaging Limit without Business Verification:",
            }),
            description: t({
              id: "whatsapp-account-page.messaging-limit-without-business-verification-description",
              defaultMessage:
                "If your business is not verified, your business-initiated messaging limit is 50 contacts per 24-hours.",
            }),
          },
        ]}
      />
    </DataLoadingWrapper>
  );
};
